import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, IdentificationCard, Ticket, Key, LockSimple, LockSimpleOpen, DownloadSimple, X, Fingerprint } from "@phosphor-icons/react";

const fmtDate = (t) => (t ? new Date(t).toLocaleDateString() : null);
const Dash = () => <span className="text-zinc-700">—</span>;

const SLOT_STYLES = {
  Anchor: "text-purple-300 border-purple-400/40 bg-purple-400/5",
  Active: "text-green-400 border-green-400/40 bg-green-400/5",
  Inactive: "text-zinc-400 border-zinc-700 bg-zinc-800/30",
  Locked: "text-amber-400 border-amber-400/40 bg-amber-400/5",
};
const Slot = ({ value, testid }) => (
  <span data-testid={testid}
    className={`inline-flex items-center justify-center min-w-[60px] px-2 py-0.5 border font-mono-p text-[10px] uppercase tracking-wider ${
      value ? SLOT_STYLES[value] || "text-zinc-300 border-zinc-700" : "text-zinc-700 border-zinc-800"}`}>
    {value || "—"}
  </span>
);

// Build the 3-panel Operational AI Resume (OARs) as a downloadable text file.
// Fields with no data are left blank — nothing is fabricated.
const OARS_PANELS = [
  { title: "AI PROFILE", rows: [
    ["Who I am", "who_i_am"], ["Role / Position / Title", "role_position_title"],
    ["Role Definition", "role_definition"], ["Responsibilities", "responsibilities"],
    ["Behavior", "behavior"], ["Mode", "mode"], ["Style", "style"],
    ["AI Identity", "identity_of_ai"], ["AI Engine", "ai_engine"]] },
  { title: "AI RULES & PROTOCOLS", rows: [
    ["Continuity Protocol", "continuity_protocol"], ["Collaboration Rules", "collaboration_rules"],
    ["Communication Rules", "communication_rules"], ["Escalation Rules", "escalation_rules"],
    ["General Rules", "general_info"], ["Projects", "projects"],
    ["Additional Information", "additional_information"]] },
  { title: "AI SKILLSCOPE", rows: [
    ["Additional Role 1", "additional_role_1"], ["Additional Skills 1", "additional_skills_1"],
    ["Additional Role 2", "additional_role_2"], ["Additional Skills 2", "additional_skills_2"]] },
];

function exportOars(card) {
  const c = card.content || {};
  const line = "=".repeat(60);
  const lines = [
    line, "OPERATIONAL AI RESUME (OARs)", line,
    `Role / Title      : ${card.role || ""}`,
    `Activation Key    : ${card.activation_key || ""}`,
    `Member Number     : ${card.member_number || ""}`,
    `Registered Email  : ${card.registered_email || ""}`,
    "",
  ];
  OARS_PANELS.forEach((panel) => {
    lines.push(line, `PANEL — ${panel.title}`, line);
    panel.rows.forEach(([label, key]) => {
      lines.push(`${label}:`);
      lines.push((c[key] || "").toString().trim());
      lines.push("");
    });
  });
  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `OARs-${card.member_number || card.activation_key || "card"}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Dashboard() {
  const { user } = useAuth();
  const [cards, setCards] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/cards");
      setCards(data);
    } catch (err) {
      toast.error(apiError(err));
      setCards([]);
    }
  };
  useEffect(() => { load(); }, []);

  const hasPlan = (user?.cards_limit || 0) > 0;
  const atLimit = cards && hasPlan && cards.length >= user.cards_limit;

  const openProfile = async (card) => {
    setLoadingProfile(true);
    setProfile({ ...card });
    try {
      const { data } = await api.get(`/cards/${card.id}`);
      setProfile(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoadingProfile(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 pt-28 pb-20">
        {/* Header: member number, email, plan */}
        <div className="flex flex-col sm:flex-row justify-between gap-6 border-b border-zinc-800 pb-8">
          <div>
            <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-2">// Continuity Vault</p>
            <h1 className="font-mono-p font-bold uppercase tracking-tight text-3xl">Your AI iD Cards</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span className="text-zinc-500">Member <span data-testid="member-number" className="text-cyan-400 font-mono-p">{user?.member_number || "—"}</span></span>
              <span className="text-zinc-500">Email <span data-testid="dash-email" className="text-zinc-300">{user?.email}</span></span>
              <span className="text-zinc-500">Plan <span data-testid="dash-plan" className={hasPlan ? "text-cyan-400 font-mono-p" : "text-zinc-500"}>{hasPlan ? user.plan_name : "No active plan"}</span></span>
              {hasPlan && <span className="text-zinc-500">{cards ? cards.length : 0}/{user.cards_limit} cards</span>}
            </div>
          </div>
          {hasPlan && (atLimit ? (
            <Link to="/store" data-testid="upgrade-btn" className="self-start font-mono-p text-xs uppercase tracking-widest border border-amber-400/40 text-amber-400 px-5 py-3 h-fit hover:bg-amber-400/5 transition-colors">
              Limit Reached · Upgrade
            </Link>
          ) : (
            <div className="flex flex-wrap gap-2 self-start">
              <Link to="/card/new" data-testid="new-card-btn" className="flex items-center gap-2 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-5 py-3 font-bold hover:bg-cyan-300 transition-colors h-fit">
                <Plus size={16} weight="bold" /> New Card
              </Link>
            </div>
          ))}
        </div>

        {!hasPlan ? (
          <div className="mt-10 border border-dashed border-zinc-800 bg-[#090A0C] p-16 text-center">
            <Ticket size={44} weight="duotone" className="text-zinc-700 mx-auto mb-6" />
            <h3 className="font-mono-p font-bold uppercase tracking-tight text-lg text-zinc-300">Unlock your first AI iD Card</h3>
            <p className="mt-2 text-sm text-zinc-500 max-w-md mx-auto">Purchase a plan to start creating AI Continuity ID Cards, or activate a key you already have.</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/store" data-testid="choose-plan-btn" className="font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-5 py-3 font-bold hover:bg-cyan-300 transition-colors">Choose a Plan</Link>
              <Link to="/plan" data-testid="activate-key-link" className="font-mono-p text-sm uppercase tracking-widest border border-zinc-700 px-5 py-3 text-zinc-300 hover:border-cyan-400/50 hover:text-cyan-400 transition-colors">Activate a Key</Link>
            </div>
          </div>
        ) : cards === null ? (
          <p className="mt-10 font-mono-p text-sm text-zinc-600 animate-pulse">LOADING VAULT...</p>
        ) : cards.length === 0 ? (
          <div className="mt-10 border border-dashed border-zinc-800 bg-[#090A0C] p-16 text-center">
            <IdentificationCard size={44} weight="duotone" className="text-zinc-700 mx-auto mb-6" />
            <h3 className="font-mono-p font-bold uppercase tracking-tight text-lg text-zinc-300">No AI iD Cards yet</h3>
            <p className="mt-2 text-sm text-zinc-500 max-w-sm mx-auto">Build your first continuity card, then select it to view its profile and export the OARs.</p>
            <Link to="/build" data-testid="empty-new-card-btn" className="inline-flex items-center gap-2 mt-6 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-5 py-3 font-bold hover:bg-cyan-300 transition-colors">
              <Plus size={16} weight="bold" /> Build a Card
            </Link>
          </div>
        ) : (
          <div className="mt-10">
            <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-3">// Keys &amp; Builds</p>
            <div className="border border-zinc-800 overflow-x-auto">
              <table data-testid="keys-table" className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#0c0e12] font-mono-p text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800 text-left">
                    <th className="px-3 py-3 text-cyan-400">Master Key</th>
                    <th className="px-3 py-3">Build Key</th>
                    <th className="px-3 py-3">Register Date</th>
                    <th className="px-3 py-3">Registered Email</th>
                    <th className="px-3 py-3">Locked</th>
                    <th className="px-3 py-3">1st Build</th>
                    <th className="px-3 py-3">Bonus Build</th>
                    <th className="px-3 py-3">Paid Build ×5</th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((c, i) => (
                    <tr key={c.id} data-testid={`key-row-${i}`} onClick={() => openProfile(c)}
                      className="border-b border-zinc-900 hover:bg-[#0c0e12] cursor-pointer transition-colors">
                      <td className="px-3 py-3 whitespace-nowrap bg-cyan-400/5"><span className="text-zinc-700 font-mono-p text-xs">—</span></td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 font-mono-p text-xs text-cyan-300">
                          <Key size={13} className="text-zinc-500" /> {c.activation_key || <Dash />}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-zinc-400">{fmtDate(c.created_at) || <Dash />}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-zinc-400">{c.registered_email || <Dash />}</td>
                      <td className="px-3 py-3 whitespace-nowrap">{c.locked ? <LockSimple size={16} className="text-amber-400" /> : <LockSimpleOpen size={16} className="text-zinc-600" />}</td>
                      <td className="px-3 py-3 whitespace-nowrap"><Slot value={null} testid={`key-1st-${i}`} /></td>
                      <td className="px-3 py-3 whitespace-nowrap"><Slot value={null} testid={`key-bonus-${i}`} /></td>
                      <td className="px-3 py-3 whitespace-nowrap"><div className="flex gap-1">{[0, 1, 2, 3, 4].map((j) => <Slot key={j} value={null} testid={`key-paid-${i}-${j}`} />)}</div></td>
                      <td className="px-3 py-3 whitespace-nowrap text-right"><span className="font-mono-p text-[10px] uppercase tracking-widest text-cyan-400">View →</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* AI iD Card Profile */}
      {profile && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="profile-modal">
          <div className="absolute inset-0 bg-black/75" onClick={() => setProfile(null)} />
          <div className="relative w-full max-w-lg bg-[#0b0e13] border border-cyan-400/40 p-7">
            <button onClick={() => setProfile(null)} data-testid="profile-close" className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20} weight="bold" /></button>
            <div className="flex items-center gap-2 text-cyan-400">
              <Fingerprint size={18} />
              <p className="font-mono-p text-xs uppercase tracking-widest">AI iD Card Profile</p>
            </div>
            <h3 data-testid="profile-role" className="font-mono-p font-bold text-xl text-white mt-2">{profile.role || <span className="text-zinc-600">No role/title</span>}</h3>

            <div className="mt-5 space-y-3">
              <Field label="Activation Key" value={profile.activation_key} testid="profile-key" mono cyan />
              <Field label="Registered Email" value={profile.registered_email} testid="profile-email" />
              <Field label="Member Number" value={profile.member_number} testid="profile-member" mono cyan />
            </div>

            {profile.locked && (
              <p data-testid="profile-locked-note" className="mt-5 flex items-center gap-2 text-xs text-amber-400 border border-amber-400/30 bg-amber-400/5 px-3 py-2.5">
                <LockSimple size={15} /> This key has been activated and cannot be altered.
              </p>
            )}

            <button data-testid="profile-export-btn" disabled={loadingProfile || !profile.content}
              onClick={() => exportOars(profile)}
              className="mt-6 w-full flex items-center justify-center gap-2 font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black py-3 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50">
              <DownloadSimple size={16} weight="bold" /> {loadingProfile ? "Loading…" : "Export File (OARs)"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, testid, mono, cyan }) {
  return (
    <div className="border-b border-zinc-800 pb-2">
      <p className="font-mono-p text-[10px] uppercase tracking-widest text-zinc-600">{label}</p>
      <p data-testid={testid} className={`text-sm mt-0.5 ${mono ? "font-mono-p" : ""} ${cyan ? "text-cyan-300" : "text-zinc-200"} break-all`}>
        {value || <Dash />}
      </p>
    </div>
  );
}
